import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Box, TextField, Button, Typography, CircularProgress, Paper, Stack, IconButton, List, ListItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import styles from '../styles/Dashboard.module.css';
import { getApiUrl } from '../utils/apiConfig';

// Estilos personalizados usando styled-components
const ChatContainer = styled(Paper)(({ theme }) => ({
  padding: '20px',
  maxWidth: '600px',
  margin: '0 auto',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
}));

const MessageList = styled(List)({
  maxHeight: '400px',
  overflowY: 'auto',
  marginBottom: '20px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',
    borderRadius: '3px',
  },
});

const UserMessage = styled(ListItem)({
  justifyContent: 'flex-end',
  textAlign: 'right',
});

const IAMessage = styled(ListItem)({
  justifyContent: 'flex-start',
  textAlign: 'left',
});

const MessageBubble = styled(Box)(({ theme, sender }) => ({
  maxWidth: '70%',
  padding: '10px 16px',
  borderRadius: sender === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0',
  backgroundColor: sender === 'user' ? theme.palette.primary.main : theme.palette.grey[300],
  color: sender === 'user' ? theme.palette.common.white : theme.palette.text.primary,
  animation: sender === 'ia' ? 'fadeIn 0.5s ease' : 'none',
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}));

const ChatIA = () => {
  const [pergunta, setPergunta] = useState('');
  const initialMessage = { role: 'assistant', content: 'Olá! Como posso ajudar com o regulamento do seu condomínio hoje?' };
  const [messages, setMessages] = useState([initialMessage]);
  const [condominioId, setCondominioId] = useState(null);
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  // Obtém o usuário logado automaticamente durante a autenticação
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Consulta o condominio_id do usuário no Firestore
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCondominioId(userDoc.data().condominio_id);
        } else {
          console.error('Erro: Dados do usuário não encontrados no Firestore.');
        }
      }
    });

    // Limpa o listener ao desmontar
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handlePerguntar = async () => {
    if (!pergunta.trim()) {
      return;
    }
    if (!condominioId) {
      console.error('Condomínio ID não encontrado.');
      return;
    }

    const novaPergunta = pergunta;
    // Adiciona a pergunta do usuário e um placeholder vazio para a resposta da IA
    const newMessages = [...messages, { role: 'user', content: novaPergunta }, { role: 'assistant', content: '' }];
    setMessages(newMessages);
    setPergunta('');
    setLoading(true);

    try {
      if (!auth.currentUser) {
        throw new Error('Usuário não autenticado.');
      }
      const token = await auth.currentUser.getIdToken();

      if (!token) {
        throw new Error('Token de autenticação não encontrado.');
      }


      // Envia a pergunta e o condominio_id para o backend

      // ...
      const response = await fetch(`${getApiUrl()}/perguntar-ia`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // Envia o histórico sem o placeholder da resposta da IA
        body: JSON.stringify({ messages: newMessages.slice(0, -1), condominio_id: condominioId }),
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      // Processa a resposta em streaming
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data:')) {
            try {
              const data = JSON.parse(line.substring(5));
              setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                const updatedLastMessage = { ...lastMessage, content: lastMessage.content + data.chunk };
                return [...prev.slice(0, -1), updatedLastMessage];
              });
            } catch (e) {
              console.error("Erro ao processar chunk do stream:", e);
            }
          }
        }
      }

    } catch (error) {
      console.error('Erro ao enviar pergunta:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro ao processar sua pergunta.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack sx={{ height: '100%', minHeight: '500px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" className={styles.sectionTitle}>Assistente Virtual</Typography>
        <IconButton
          onClick={() => setMessages([initialMessage])}
          aria-label="limpar histórico"
          title="Limpar Histórico"
          size="small"
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Faça perguntas sobre o regulamento do seu condomínio.
      </Typography>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px', mb: 2 }}>
        {messages.map((msg, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                maxWidth: '80%',
                backgroundColor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
            </Paper>
          </Box>
        ))}
        {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto' }} />}
        <div ref={chatEndRef} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          label="Digite sua pergunta..."
          value={pergunta}
          onChange={(e) => setPergunta(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !loading && handlePerguntar()}
          disabled={loading}
          size="small"
        />
        <Button variant="contained" onClick={handlePerguntar} disabled={loading}>
          Enviar
        </Button>
      </Box>
    </Stack>
  );
};

export default ChatIA;